// ====================================================================================================
// CarrierSubmitPage.jsx
// ----------------------------------------------------------------------------------------------------
// Carrier-side bid submission page for Tenders and Tender Phases.
// - Works both in legacy tab mode (context from M_State) and wrapper mode
//   (context injected via props, used by CarrierInviteViewBidPage).
// ====================================================================================================

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { LoadingContext, MHeader, MButton } from "src/M_Components";
import { BufferService } from "src/M_Components/M_Utils/buffer.service";
import InviteTableSection from "./InviteTableSection";
import { isFailurePayload, apiMessage } from "src/components/utils/apiResponse";
import "./CarrierSubmitPage.css";

// ====================================================================================================
// Helper: extract a value from M_State by key
// ====================================================================================================
const pickFromMState = (M_State, key) =>
  M_State.find((o) => o.key === key)?.value ?? null;

// ====================================================================================================
// Helper: resolve CarrierId in the carrier area
// ----------------------------------------------------------------------------------------------------
// Resolution order:
//   1) M_State["CarrierId"]
//   2) LoadingContext.carrierId
//   3) localStorage "app.auth.carrier.session"
// ====================================================================================================
const useCarrierId = (M_State, ctx) => {
  const carrierIdFromState = useMemo(
    () => M_State.find((o) => o.key === "CarrierId")?.value ?? null,
    [M_State]
  );

  const carrierSession = useMemo(() => {
    try {
      const raw = localStorage.getItem("app.auth.carrier.session");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const carrierId =
    carrierIdFromState ?? ctx.carrierId ?? carrierSession?.CarrierId ?? null;

  return carrierId != null ? Number(carrierId) : null;
};

// ====================================================================================================
// Helper: load all pages of quotes given a BufferService endpoint and an OData-style filter
// ----------------------------------------------------------------------------------------------------
// Handles different envelope shapes (Items/items/Data/data/array) and stops when all pages are read.
// ====================================================================================================
const useLoadAllPages = () => {
  const loadAllPages = useCallback(async (svc, filter) => {
    const pageSize = 200;
    let page = 1;
    const acc = [];
    const seen = new Set();

    while (true) {
      const qs = `?Page=${page}&PageSize=${pageSize}&filter=${encodeURIComponent(
        filter
      )}`;
      const res = await svc.filter(qs);

      if (isFailurePayload(res)) {
        break;
      }

      const items = Array.isArray(res?.Items)
        ? res.Items
        : Array.isArray(res?.items)
          ? res.items
          : Array.isArray(res?.Data)
            ? res.Data
            : Array.isArray(res?.data)
              ? res.data
              : Array.isArray(res)
                ? res
                : [];

      if (!items.length) {
        break;
      }

      for (const it of items) {
        const id = it?.Id ?? it?.id;
        if (id != null && !seen.has(id)) {
          seen.add(id);
          acc.push(it);
        }
      }

      const totalPages =
        Number(
          res?.TotalPages ??
          res?.totalPages ??
          res?.Total ??
          res?.total ??
          (items.length < pageSize ? page : page + 1)
        ) || page;

      if (page >= totalPages) {
        break;
      }
      page += 1;
    }

    return acc;
  }, []);

  return loadAllPages;
};

// ====================================================================================================
// Helper: map /flat lane items into the lane shape expected by InviteTableSection
// ----------------------------------------------------------------------------------------------------
// Keeps compatibility with existing table logic by providing:
//   - LaneId
//   - Origin / Destination
//   - LaneSource / LaneDestination
//   - IsActive (derived from inactive flags when available)
// ====================================================================================================
const mapFlatLaneToGridLane = (it) => {
  const laneId = it?.laneId ?? it?.LaneId ?? it?.id ?? it?.Id ?? null;

  const origin = String(it?.sourceLocation ?? it?.SourceLocation ?? "").trim();
  const destination = String(
    it?.destinationLocation ?? it?.DestinationLocation ?? ""
  ).trim();

  return {
    ...it,

    // Legacy identifiers
    LaneId: laneId != null ? Number(laneId) : null,

    // Legacy "names"
    Origin: origin,
    Destination: destination,
    LaneSource: origin,
    LaneDestination: destination,

    // Legacy "active" flag
    IsActive:
      it?.inactive != null
        ? !it.inactive
        : it?.Inactive != null
          ? !it.Inactive
          : true,
  };
};

// ====================================================================================================
// CarrierSubmitPage component
// ----------------------------------------------------------------------------------------------------
// Supports two hosting models:
//   1) Legacy tab mode:
//        - TenderConfigCrud writes CarrierBidContext/CarrierBidTender into M_State.
//        - Tabs system opens "/tenders/CarrierSubmitPage" in a new tab.
//        - This component reads from M_State and calls handleTabClose on success.
//
//   2) Wrapper mode (CarrierInviteViewBidPage):
//        - Wrapper keeps TenderConfigCrud + CarrierSubmitPage in the same tab.
//        - It passes bidContext / bidHeader and onBack via props.
//        - This component uses the provided props instead of reading bid configuration from M_State.
// ====================================================================================================
export default function CarrierSubmitPage({
  handleTabClose,
  toastMessages,
  // Optional direct context injection (wrapper mode)
  bidContext: bidContextProp,
  bidHeader: bidHeaderProp,
  // Optional back handler (wrapper mode): go back to TenderConfigCrud without closing the tab
  onBack,
}) {
  const { t } = useTranslation();
  const ctx = useContext(LoadingContext);
  const M_State = useSelector((s) => s.StateKeyValue);

  const carrierId = useCarrierId(M_State, ctx);
  const loadAllPages = useLoadAllPages();

  // --------------------------------------------------------------------------------------------------
  // Toast and loading helpers stored in refs
  // --------------------------------------------------------------------------------------------------
  const toastRef = useRef(ctx.toastMessages);
  const setLoadRef = useRef(ctx.setIsLoad);

  // Counts concurrent async loads to avoid flicker and premature hiding of the global overlay.
  const loadingCounterRef = useRef(0);

  const beginGlobalLoading = useCallback(() => {
    loadingCounterRef.current += 1;

    // Only turn on when the first async load starts
    if (loadingCounterRef.current === 1) {
      setLoadRef.current?.(true);
    }
  }, []);

  const endGlobalLoading = useCallback(() => {
    // Defensive: never let the counter go below zero
    loadingCounterRef.current = Math.max(0, loadingCounterRef.current - 1);

    // Only turn off when the last async load finishes
    if (loadingCounterRef.current === 0) {
      setLoadRef.current?.(false);
    }
  }, []);

  useEffect(() => {
    // toastMessages prop can override the toast implementation from context when provided
    toastRef.current = toastMessages || ctx.toastMessages;
  }, [ctx.toastMessages, toastMessages]);

  useEffect(() => {
    // Keep a stable reference to the loading toggle function from context
    setLoadRef.current = ctx.setIsLoad;
  }, [ctx.setIsLoad]);

  useEffect(() => {
    // Defensive: never leave the global overlay on when unmounting
    return () => {
      loadingCounterRef.current = 0;
      setLoadRef.current?.(false);
    };
  }, []);

  // --------------------------------------------------------------------------------------------------
  // bidContext / bidHeader (props take precedence over M_State)
  // --------------------------------------------------------------------------------------------------
  const bidContext =
    bidContextProp || pickFromMState(M_State, "CarrierBidContext");
  const bidHeader =
    bidHeaderProp || pickFromMState(M_State, "CarrierBidTender");

  const scope = (bidContext?.scope || "tender").toLowerCase(); // "tender" | "phase"
  const crudMode = (bidContext?.crudMode || "create").toLowerCase(); // "create" | "edit" | "view"

  const tenderId = bidContext?.tenderId ?? null;
  const tenderPhaseId = bidContext?.tenderPhaseId ?? null;

  // "spot" uses tender-level quotes, "phase" uses phase-level quotes
  const mode = scope === "phase" ? "phase" : "spot";

  const isCreateMode = crudMode === "create";
  const isEditMode = crudMode === "edit";
  const isViewMode = crudMode === "view";

  // Table is read-only when:
  //   - crudMode is "view"
  //   - or carrierId cannot be resolved (user not authenticated or invalid session)
  const tableReadOnly = isViewMode || !carrierId;

  const baseUrl = ctx.DEFAULTURL;

  // --------------------------------------------------------------------------------------------------
  // Quote services (list + batch + create + update) - UNIFIED TenderQuotes
  // --------------------------------------------------------------------------------------------------
  const tenderQuotesService = useMemo(
    () => new BufferService("TenderQuotes", baseUrl),
    [baseUrl]
  );
  const tenderQuotesBatchService = useMemo(
    () => new BufferService("TenderQuotes/create/batch", baseUrl),
    [baseUrl]
  );
  const tenderQuoteCreateService = useMemo(
    () => new BufferService("TenderQuotes/create", baseUrl),
    [baseUrl]
  );
  const tenderQuoteUpdateService = useMemo(
    () => new BufferService("TenderQuotes/update", baseUrl),
    [baseUrl]
  );

  // Options services used to load basic Tender and TenderPhase metadata
  const tenderOptionsService = useMemo(
    () => new BufferService("Tenders", baseUrl),
    [baseUrl]
  );
  const tenderPhaseOptionsService = useMemo(
    () => new BufferService("TenderPhases", baseUrl),
    [baseUrl]
  );

  // Lanes are loaded from a unified endpoint for both Spot and Non-Spot:
  //   GET /flat?Page=1&PageSize=200&Filter=...
  // Used in view mode because carrier-flat hides lanes where the carrier has accepted quotes
  // (view mode is historical and must show all lanes).
  const tenderLanesFlatService = useMemo(
    () => new BufferService("TenderLanes/flat", baseUrl),
    [baseUrl]
  );

  // Lanes are loaded from a carrier-filtered endpoint:
  //   GET /flat/carrier/{carrierId}?Page=1&PageSize=200&Filter=...
  // This removes the need for FE-side filtering (accepted quotes + transport types rules are enforced by BE).
  const tenderLanesCarrierFlatService = useMemo(
    () =>
      new BufferService(`TenderLanes/flat/carrier/${carrierId ?? 0}`, baseUrl),
    [baseUrl, carrierId]
  );

  // Local Tender / Phase DTOs used to extract RouteId / PhaseId when resolving lanes
  const [tender, setTender] = useState(bidContext?.tenderDto || {});
  const [tenderPhase, setTenderPhase] = useState(
    bidContext?.tenderPhaseDto || {}
  );

  const [tenderSchemaOptions, setTenderSchemaOptions] = useState([]);
  const [phaseSchemaOptions, setPhaseSchemaOptions] = useState([]);
  const [schemasLoading, setSchemasLoading] = useState(false);

  // Lanes:
  //   - Initial seed comes from bidContext.lanes (important for invite flow)
  //   - Later replaced by the API result (Route or TenderPhaseLaneRels)
  const [lanes, setLanes] = useState(() => bidContext?.lanes || []);

  const [prefillQuotes, setPrefillQuotes] = useState([]);
  const [quotesLoading, setQuotesLoading] = useState(false);

  // --------------------------------------------------------------------------------------------------
  // Stores accepted quotes information to support remaining capacity per lane
  // --------------------------------------------------------------------------------------------------
  const [acceptedLoadsByLane, setAcceptedLoadsByLane] = useState({});

  // --------------------------------------------------------------------------------------------------
  // Derives the tender loads capacity from the available context DTOs when present
  // --------------------------------------------------------------------------------------------------
  const tenderLoads =
    Number(
      bidContext?.tenderDto?.Loads ??
      bidContext?.tenderDto?.loads ??
      bidContext?.tenderPhaseDto?.Tender?.Loads ??
      bidContext?.tenderPhaseDto?.tender?.Loads ??
      bidHeader?.Loads ??
      tender?.Loads ??
      tender?.loads ??
      0
    ) || 0;

  // --------------------------------------------------------------------------------------------------
  // submitServices (batch submit in create mode)
  // --------------------------------------------------------------------------------------------------
  const submitServices = useMemo(
    () => ({
      // Spot : batch without tenderPhaseId
      spotBatch: {
        post: async (req) => {
          const items = Array.isArray(req?.items) ? req.items : [];
          if (!tenderId || !carrierId) {
            return {
              IsFailure: true,
              Message: "Missing tenderId or carrierId for batch submission.",
            };
          }

          const body = {
            tenderId: Number(tenderId),
            carrierId: Number(carrierId),
            items: items.map((it) => ({
              laneId: Number(it.laneId ?? it.LaneId ?? it.laneID ?? 0),
              bidValue: Number(it.bidValue ?? it.BidValue ?? 0),
              loads: Number(it.loads ?? it.Loads ?? 0),
              // Forward estimatedTransitDays from the grid batch payload
              estimatedTransitDays: Number(
                it.estimatedTransitDays ??
                it.EstimatedTransitDays ??
                it.etd ??
                0
              ),
            })),
          };

          return tenderQuotesBatchService.post(body);
        },
      },

      // Phase (non-spot) : batch com tenderPhaseId (e tenderId opcional)
      phaseBatch: {
        post: async (req) => {
          const items = Array.isArray(req?.items) ? req.items : [];
          if (!tenderPhaseId || !carrierId) {
            return {
              IsFailure: true,
              Message:
                "Missing tenderPhaseId or carrierId for batch submission.",
            };
          }

          const body = {
            // tenderId é opcional no batch, usa-se se existir no contexto
            ...(tenderId != null ? { tenderId: Number(tenderId) } : {}),
            tenderPhaseId: Number(tenderPhaseId),
            carrierId: Number(carrierId),
            items: items.map((it) => ({
              laneId: Number(it.laneId ?? it.LaneId ?? it.laneID ?? 0),
              bidValue: Number(it.bidValue ?? it.BidValue ?? 0),
              loads: Number(it.loads ?? it.Loads ?? 0),
              // Forward estimatedTransitDays from the grid batch payload
              estimatedTransitDays: Number(
                it.estimatedTransitDays ??
                it.EstimatedTransitDays ??
                it.etd ??
                0
              ),
            })),
          };

          return tenderQuotesBatchService.post(body);
        },
      },
    }),
    [carrierId, tenderQuotesBatchService, tenderId, tenderPhaseId]
  );

  // --------------------------------------------------------------------------------------------------
  // editServices (single quote create/update in edit mode)
  // --------------------------------------------------------------------------------------------------
  // InviteTableSection constrói o body completo (laneId, bidValue, loads, estimatedTransitDays,
  // carrierId, tenderId/tenderPhaseId). Aqui apenas decidimos o endpoint e forçamos inactive = false.
  const editServices = useMemo(
    () => ({
      spot: {
        create: (payload) => tenderQuoteCreateService.post?.(payload),
        update: (id, payload) =>
          tenderQuoteUpdateService.update?.(id, {
            ...payload,
            inactive: false,
          }),
      },
      phase: {
        create: (payload) => tenderQuoteCreateService.post?.(payload),
        update: (id, payload) =>
          tenderQuoteUpdateService.update?.(id, {
            ...payload,
            inactive: false,
          }),
      },
    }),
    [tenderQuoteCreateService, tenderQuoteUpdateService]
  );

  // --------------------------------------------------------------------------------------------------
  // Load schemas for Tender and TenderPhase
  // Values are used for metadata and are not rendered directly in this page
  // --------------------------------------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;

    const loadSchemas = async () => {
      setSchemasLoading(true);
      beginGlobalLoading();

      try {
        const [tenderOpts, phaseOpts] = await Promise.allSettled([
          tenderOptionsService.options?.(),
          tenderPhaseOptionsService.options?.(),
        ]);

        if (!cancelled) {
          if (
            tenderOpts.status === "fulfilled" &&
            !isFailurePayload(tenderOpts.value)
          ) {
            setTenderSchemaOptions(
              Array.isArray(tenderOpts.value) ? tenderOpts.value : []
            );
          } else {
            setTenderSchemaOptions([]);
          }

          if (
            phaseOpts.status === "fulfilled" &&
            !isFailurePayload(phaseOpts.value)
          ) {
            setPhaseSchemaOptions(
              Array.isArray(phaseOpts.value) ? phaseOpts.value : []
            );
          } else {
            setPhaseSchemaOptions([]);
          }
        }
      } catch (err) {
        if (!cancelled) {
          toastRef.current?.(
            "error",
            "",
            apiMessage(err, t("ErrorLoadingData"))
          );
        }
      } finally {
        endGlobalLoading();

        if (!cancelled) {
          setSchemasLoading(false);
        }
      }
    };

    loadSchemas();

    return () => {
      cancelled = true;
    };
  }, [tenderOptionsService, tenderPhaseOptionsService, t, beginGlobalLoading, endGlobalLoading]);


  // --------------------------------------------------------------------------------------------------
  // Load lanes:
  //   - Spot (mode === "spot"): lanes still open for the tender (IsSatisfied eq false)
  //   - Phase (mode === "phase"): lanes still open for the tender (IsSatisfied eq false)
  //   - Se não houver IDs, usamos bidContext.lanes (invite flow) ou vazio
  // --------------------------------------------------------------------------------------------------
  // --------------------------------------------------------------------------------------------------
  // Load lanes:
  //   - Spot (mode === "spot"): lanes still open for the tender (IsSatisfied eq false)
  //   - Phase (mode === "phase"): lanes still open for the tender (IsSatisfied eq false)
  //   - Se não houver IDs, usamos bidContext.lanes (invite flow) ou vazio
  // --------------------------------------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;

    const keepInviteSeedOrClear = () => {
      if (Array.isArray(bidContext?.lanes) && bidContext.lanes.length) {
        setLanes(bidContext.lanes);
      } else {
        setLanes([]);
      }
    };

    const resolveTenderId = () => {
      const fromContext =
        bidContext?.tenderId ??
        bidContext?.tenderDto?.Id ??
        bidContext?.tenderDto?.id ??
        bidContext?.tenderPhaseDto?.TenderId ??
        bidContext?.tenderPhaseDto?.tenderId ??
        bidContext?.tenderPhaseDto?.Tender?.Id ??
        bidContext?.tenderPhaseDto?.tender?.Id ??
        bidContext?.tenderPhaseDto?.tender?.id ??
        null;

      const fromLocal =
        tender?.Id ??
        tender?.id ??
        tenderPhase?.TenderId ??
        tenderPhase?.tenderId ??
        tenderPhase?.Tender?.Id ??
        tenderPhase?.tender?.Id ??
        tenderPhase?.tender?.id ??
        null;

      const finalId = tenderId ?? fromContext ?? fromLocal ?? null;
      return finalId != null ? Number(finalId) : null;
    };

    const loadLanes = async () => {
      const resolvedTenderId = resolveTenderId();

      if (!resolvedTenderId) {
        keepInviteSeedOrClear();
        return;
      }

      // Choose which endpoint to use:
      // - view mode (tender finalized): use the normal flat endpoint so we can see ALL lanes (historical view),
      //   including lanes where the carrier has accepted quotes.
      // - create/edit: use carrier-flat endpoint (BE enforces eligibility rules).
      const svc = isViewMode
        ? tenderLanesFlatService
        : tenderLanesCarrierFlatService;

      // If carrierId cannot be resolved, avoid calling carrier-filtered endpoint.
      // In view mode we can still call the normal flat endpoint.
      if (!carrierId && !isViewMode) {
        keepInviteSeedOrClear();
        return;
      }

      beginGlobalLoading();

      try {
        const pageSize = 200;
        let page = 1;
        const acc = [];
        const seen = new Set();

        // NOTE:
        // - create/edit: we filter to only lanes still open at the tender level (IsSatisfied eq false)
        // - view mode: we must show ALL lanes (historical), so we DO NOT apply the IsSatisfied filter
        const filter = isViewMode
          ? `TenderId eq ${resolvedTenderId}`
          : `TenderId eq ${resolvedTenderId} and IsSatisfied eq false`;

        while (true) {
          if (cancelled) break;

          const res = await svc.filter(
            `?Page=${page}&PageSize=${pageSize}&Filter=${encodeURIComponent(
              filter
            )}`
          );

          if (cancelled) break;
          if (isFailurePayload(res)) break;

          const items = Array.isArray(res?.Items)
            ? res.Items
            : Array.isArray(res?.items)
              ? res.items
              : Array.isArray(res?.Data)
                ? res.Data
                : Array.isArray(res?.data)
                  ? res.data
                  : Array.isArray(res)
                    ? res
                    : [];

          if (!items.length) break;

          for (const it of items) {
            const laneId = it?.laneId ?? it?.LaneId ?? it?.id ?? it?.Id ?? null;
            if (laneId == null || seen.has(laneId)) continue;

            seen.add(laneId);
            const mapped = mapFlatLaneToGridLane(it);
            if (mapped?.LaneId == null) continue;
            acc.push(mapped);
          }

          const totalPages =
            Number(
              res?.TotalPages ??
              res?.totalPages ??
              (items.length < pageSize ? page : page + 1)
            ) || page;

          if (page >= totalPages) break;
          page += 1;
        }

        if (!cancelled) {
          setLanes(acc);
        }
      } catch (err) {
        if (!cancelled) {
          toastRef.current?.(
            "error",
            "",
            apiMessage(err, t("ErrorLoadingData"))
          );
          setLanes([]);
        }
      } finally {
        endGlobalLoading();
      }
    };

    loadLanes();

    return () => {
      cancelled = true;
    };
  }, [
    tenderLanesFlatService,
    tenderLanesCarrierFlatService,
    carrierId,
    tender,
    tenderPhase,
    bidContext,
    t,
    mode,
    tenderId,
    isViewMode,
    beginGlobalLoading,
    endGlobalLoading,
  ]);

  // --------------------------------------------------------------------------------------------------
  // Loads accepted quotes for the current tender to compute:
  // - The sum of accepted loads per lane (used for remaining capacity in the UI)
  // --------------------------------------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;

    const resolveTenderId = () => {
      const fromContext =
        bidContext?.tenderId ??
        bidContext?.tenderDto?.Id ??
        bidContext?.tenderDto?.id ??
        bidContext?.tenderPhaseDto?.TenderId ??
        bidContext?.tenderPhaseDto?.tenderId ??
        bidContext?.tenderPhaseDto?.Tender?.Id ??
        bidContext?.tenderPhaseDto?.tender?.Id ??
        bidContext?.tenderPhaseDto?.tender?.id ??
        null;

      const finalId = tenderId ?? fromContext ?? null;
      return finalId != null ? Number(finalId) : null;
    };

    const loadAcceptedQuotes = async () => {
      const resolvedTenderId = resolveTenderId();

      if (!resolvedTenderId || !carrierId) {
        setAcceptedLoadsByLane({});
        return;
      }

      beginGlobalLoading();

      try {
        // This query is tender-scoped so it covers accepted quotes for spot and phases.
        const filter = `TenderId eq ${resolvedTenderId} and IsAccepted eq true and Inactive eq false`;
        const list = await loadAllPages(tenderQuotesService, filter);

        if (cancelled) return;

        const loadsMap = {};

        for (const q of list) {
          const laneId = Number(q?.LaneId ?? q?.laneId ?? 0);
          if (!laneId) continue;

          const loads = Number(q?.Loads ?? q?.loads ?? 0) || 0;
          loadsMap[laneId] = (loadsMap[laneId] || 0) + loads;
        }

        setAcceptedLoadsByLane(loadsMap);
      } catch {
        if (cancelled) return;
        setAcceptedLoadsByLane({});
      } finally {
        endGlobalLoading();
      }
    };

    loadAcceptedQuotes();

    return () => {
      cancelled = true;
    };
  }, [
    bidContext,
    tenderId,
    carrierId,
    tenderQuotesService,
    loadAllPages,
    beginGlobalLoading,
    endGlobalLoading,
  ]);

  // --------------------------------------------------------------------------------------------------
  // Load existing quotes for edit/view modes (unified TenderQuotes)
  // --------------------------------------------------------------------------------------------------
  const loadQuotes = useCallback(async () => {
    if (!carrierId) {
      setPrefillQuotes([]);
      return;
    }

    if (isCreateMode) {
      setPrefillQuotes([]);
      return;
    }

    let filter = null;
    if (mode === "spot" && tenderId) {
      filter = `CarrierId eq ${carrierId} and TenderId eq ${tenderId} and Inactive eq false`;
    } else if (mode === "phase" && tenderPhaseId) {
      filter = `CarrierId eq ${carrierId} and TenderPhaseId eq ${tenderPhaseId} and Inactive eq false`;
    }

    if (!filter) {
      setPrefillQuotes([]);
      return;
    }

    setQuotesLoading(true);
    beginGlobalLoading();

    try {
      const list = await loadAllPages(tenderQuotesService, filter);

      const normalized = list
        .map((q) => {
          // API unificada: camelCase, mas toleramos PascalCase também
          const laneId = q?.LaneId ?? q?.laneId;
          const bid = q?.BidValue ?? q?.bidValue;
          const loads = q?.Loads ?? q?.loads;
          const etd =
            q?.EstimatedTransitDays ??
            q?.estimatedTransitDays ??
            q?.Etd ??
            q?.etd ??
            null;
          const quoteId = q?.QuoteId ?? q?.Id ?? q?.id ?? null;
          return {
            LaneId: laneId != null ? Number(laneId) : null,
            BidValue: bid != null ? Number(bid) : null,
            Loads: loads != null ? Number(loads) : null,
            EstimatedTransitDays: etd != null ? Number(etd) : null,
            QuoteId: quoteId != null ? Number(quoteId) : null,
          };
        })
        .filter((x) => x.LaneId != null);

      setPrefillQuotes(normalized);
    } catch (err) {
      toastRef.current?.("error", "", apiMessage(err, t("ErrorLoadingData")));
      setPrefillQuotes([]);
    } finally {
      setQuotesLoading(false);
      endGlobalLoading();
    }
  }, [
    carrierId,
    isCreateMode,
    mode,
    tenderId,
    tenderPhaseId,
    tenderQuotesService,
    loadAllPages,
    t,
    beginGlobalLoading,
    endGlobalLoading,
  ]);

  useEffect(() => {
    loadQuotes();
  }, [loadQuotes]);

  const handleRefreshQuotes = async () => {
    await loadQuotes();
  };

  // --------------------------------------------------------------------------------------------------
  // Success callback behaviour in legacy tab and wrapper modes
  //   - Legacy tab mode: handleTabClose closes the tab
  //   - Wrapper mode: onBack returns to TenderConfigCrud
  // --------------------------------------------------------------------------------------------------
  const handleBatchSubmitSuccess = useCallback(() => {
    if (typeof handleTabClose === "function") {
      handleTabClose();
    } else if (typeof onBack === "function") {
      onBack();
    }
  }, [handleTabClose, onBack]);

  // --------------------------------------------------------------------------------------------------
  // Empty state when no valid bid context is available
  //  - For spot: require tenderId
  //  - For phase: allow tenderPhaseId without tenderId
  // --------------------------------------------------------------------------------------------------
  const hasValidContext =
    !!bidContext &&
    (tenderId != null || (scope === "phase" && tenderPhaseId != null));

  if (!hasValidContext) {
    return (
      <div
        style={{
          padding: "20px",
          background: "var(--color-white)",
          minHeight: "200px",
        }}
      >
        <h2 style={{ marginBottom: "10px" }}>
          {t("CarrierBid") || "Carrier Bid"}
        </h2>
        <p>{t("No bid context was found for this tab.")}</p>
      </div>
    );
  }

  return (
    <>
      {/* Header bar aligned with the internal pages style */}
      <MHeader
        element={
          <>
            {/* Optional back button when hosted inside a wrapper (CarrierInviteViewBidPage) */}
            {onBack && (
              <MButton
                tooltip={t("Back") || "Back"}
                icon={<i className="pi pi-arrow-left iconTable"></i>}
                onClick={onBack}
                label={t("Back") || "Back"}
                labelColor={"var(--color-red)"}
              />
            )}

            {/* Manual refresh for the quote grid (reloads existing quotes from the API) */}
            {!isCreateMode && (
              <MButton
                tooltip={t("RefreshTable")}
                icon={<i className="pi pi-refresh iconTable"></i>}
                onClick={handleRefreshQuotes}
                label={t("RefreshTable")}
              />
            )}
          </>
        }
      />

      <div
        style={{
          marginBottom: "10px",
          padding: "20px",
          background: "var(--color-white)",
        }}
      >
        <div className="firstDivContainer">
          {/* Quotes grid section using InviteTableSection as the bid matrix UI */}
          <InviteTableSection
            token={null} // Not used in this internal flow
            mode={mode} // "spot" | "phase"
            lanes={lanes || []}
            onRefresh={handleRefreshQuotes}
            submitServices={submitServices}
            editServices={editServices}
            crudMode={crudMode}
            readOnly={tableReadOnly}
            prefillQuotes={prefillQuotes}
            tenderId={tenderId}
            tenderPhaseId={tenderPhaseId}
            carrierId={carrierId}
            tenderLoads={tenderLoads}
            acceptedLoadsByLane={acceptedLoadsByLane}
            // Success callback:
            //   - Legacy tab mode: handleTabClose closes the tab
            //   - Wrapper mode: onBack returns to TenderConfigCrud
            onBatchSubmitSuccess={handleBatchSubmitSuccess}
          />
        </div>
      </div>
    </>
  );
}

CarrierSubmitPage.propTypes = {
  handleTabClose: PropTypes.func,
  toastMessages: PropTypes.func,
  // Optional direct context injection (wrapper mode)
  bidContext: PropTypes.shape({
    scope: PropTypes.oneOf(["tender", "phase"]),
    crudMode: PropTypes.oneOf(["create", "edit", "view"]),
    tenderId: PropTypes.number,
    tenderPhaseId: PropTypes.number,
    tenderDto: PropTypes.object,
    tenderPhaseDto: PropTypes.object,
    lanes: PropTypes.array,
  }),
  bidHeader: PropTypes.shape({
    Id: PropTypes.number,
    Name: PropTypes.string,
  }),
  // Optional back handler shown in the header in wrapper mode
  onBack: PropTypes.func,
};
